import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SOCIAL, PAYMENTS } from "./brandIcons";
import { analyticsEnabled, openCookieSettings } from "./Consent";
import { Logo } from "./Logo";
import { Slogan } from "./Slogan";
import { FOOTER_SHOP, FOOTER_INFO, FOOTER_MARKET } from "@/data/shop";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Col = ({ title, items, links }) => (
  <div>
    <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A5E00] mb-4">{title}</h4>
    <ul className="space-y-2.5">
      {items.map((i) => (
        <li key={i}>
          {links ? (
            <Link to={links[i]} data-testid={`footer-link-${slug(i)}`} className="text-sm text-[#202A35]/80 hover:text-[#202A35] hover:underline transition-colors">{i}</Link>
          ) : (
            <span data-testid={`footer-placeholder-${slug(i)}`} title="Page coming soon" aria-disabled="true"
              className="text-sm text-[#202A35]/55 cursor-not-allowed inline-flex items-center gap-2">
              {i} <span className="text-[10px] uppercase tracking-wider border border-[#202A35]/25 rounded px-1.5 py-px">Soon</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Social = ({ links }) => {
  const shown = SOCIAL.filter(([k]) => links[k]);
  if (!shown.length) return null;
  return (
    <div className="mt-6 flex gap-2" data-testid="footer-social">
      {shown.map(([k, label, Icon]) => (
        <a key={k} href={links[k]} target="_blank" rel="noopener noreferrer" aria-label={`ShopaBee on ${label}`} data-testid={`footer-social-${k}`}
          className="w-10 h-10 rounded-full bg-[#202A35] text-[#FFF3C6] flex items-center justify-center hover:bg-[#E2A100] hover:text-[#202A35] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200">
          <Icon size={17} />
        </a>
      ))}
    </div>
  );
};

const Payments = ({ ids }) => {
  const shown = PAYMENTS.filter(([k]) => ids.includes(k));
  if (!shown.length) return null;
  return (
    <div className="mt-6" data-testid="footer-payments">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8A5E00] mb-2">We accept</p>
      <ul className="flex flex-wrap gap-2">
        {shown.map(([k, label, Icon]) => (
          <li key={k} title={label} data-testid={`footer-payment-${k}`} className="h-8 px-2.5 rounded-md bg-white border border-[#202A35]/15 flex items-center gap-1.5 text-[#202A35]">
            <Icon size={k === "visa" || k === "amex" ? 30 : 20} aria-hidden="true" /><span className="sr-only">{label}</span>
            {k === "clearpay" && <span className="text-[11px] font-bold">Clearpay</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Footer = () => {
  const [site, setSite] = useState({ social: {}, payments: [] });
  useEffect(() => { api.get("/site-settings").then((r) => setSite(r.data)).catch(() => {}); }, []);
  return (
  <footer className="bg-[#FFF3C6] text-[#202A35]" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
      <div className="flex flex-col">
        <Logo testId="footer-logo" />
        <p className="mt-3 font-display text-lg font-semibold text-[#202A35]" data-testid="footer-tagline"><Slogan tone="onYellow" /></p>
        <div className="md:mt-auto" data-testid="footer-badges">
          <Social links={site.social || {}} />
          <Payments ids={site.payments || []} />
        </div>
      </div>
      <Col title="The B Marketplace" items={FOOTER_MARKET.map(([n]) => n)} links={Object.fromEntries(FOOTER_MARKET)} />
      <Col title="Shop" items={FOOTER_SHOP.map((c) => c.name)} links={Object.fromEntries(FOOTER_SHOP.map((c) => [c.name, `/shop/${c.id}`]))} />
      <Col title="Information" items={FOOTER_INFO} links={Object.fromEntries(FOOTER_INFO.map((n) => [n, n === "My Account" ? "/account" : `/info/${slug(n).replace(/-$/, "")}`]))} />
    </div>
    <div className="border-t border-[#202A35]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-[#202A35]/65 flex flex-col sm:flex-row justify-between gap-2">
        <span data-testid="footer-copyright">© 2026 ShopaBee. All rights reserved.</span>
        {analyticsEnabled && <button onClick={openCookieSettings} className="text-left sm:text-right underline hover:text-[#202A35]" data-testid="footer-cookie-settings">Cookie settings</button>}
      </div>
    </div>
  </footer>
  );
};
